import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const primaryNav = [
    { path: '/capabilities', label: 'Capabilities' },
    { path: '/products', label: 'Products' },
    { path: '/patent-portfolio', label: 'Patent Portfolio' },
    { path: '/licensing', label: 'Licensing' },
    { path: '/ai-fabric-assurance', label: 'AI Fabric' },
    { path: '/resources', label: 'Resources' },
    { path: '/news.html', label: 'Field Watch' },
  ];

  const moreNav = [
    { path: '/news-updates', label: 'News & Updates' },
    { path: '/about', label: 'About' },
    { path: '/commitment-to-society.html', label: 'Commitment to Society' },
    { path: '/vikrant-salar', label: 'Founder & Principal' },
    { path: '/investor-relations', label: 'Investor Relations' },
    { path: '/media-investor-relations', label: 'Media / Investors' },
    { path: '/patent-demos', label: 'Patent Demos' },
    { path: '/total-solution-architecture', label: 'Total Solution' },
    { path: '/salar-resilience-terrain', label: 'Interactive Demo' },
    { path: '/after-the-end-of-life', label: 'Legacy Sustainment Study' },
    { path: '/salar-graph', label: 'Salar Graph Research' },
    { path: '/engineering-advancement-medical-science', label: 'Medical Science Research' },
    { path: '/environmental-research', label: 'Environmental Research' },
    { path: '/spectrum-strike-demo', label: 'Spectrum Strike Demo' },
    { path: '/mission-assurance', label: 'Mission Assurance' },
    { path: '/government-defense', label: 'Government & Defense' },
    { path: '/international-procurement', label: 'International' },
    { path: '/publications', label: 'Publications' },
    { path: '/whitepapers', label: 'Whitepapers' },
    { path: '/compliance', label: 'Compliance' },
  ];

  const mobileNav = [
    { path: '/news.html', label: 'Field Watch' },
    { path: '/about', label: 'About' },
    { path: '/commitment-to-society.html', label: 'Commitment to Society' },
    { path: '/vikrant-salar', label: 'Founder & Principal' },
    { path: '/capabilities', label: 'Capabilities' },
    { path: '/products', label: 'Products' },
    { path: '/investor-relations', label: 'Investor Relations' },
    { path: '/media-investor-relations', label: 'Media / Investors' },
    { path: '/patent-demos', label: 'Patent Demos' },
    { path: '/total-solution-architecture', label: 'Total Solution' },
    { path: '/salar-resilience-terrain', label: 'Interactive Demo' },
    { path: '/after-the-end-of-life', label: 'Legacy Sustainment Study' },
    { path: '/salar-graph', label: 'Salar Graph Research' },
    { path: '/spectrum-strike-demo', label: 'Spectrum Strike Demo' },
    { path: '/mission-assurance', label: 'Mission Assurance' },
    { path: '/ai-fabric-assurance', label: 'AI Fabric' },
    { path: '/patent-portfolio', label: 'Patent Portfolio' },
    { path: '/licensing', label: 'Licensing' },
    { path: '/government-defense', label: 'Government & Defense' },
    { path: '/international-procurement', label: 'International' },
    { path: '/publications', label: 'Publications' },
    { path: '/whitepapers', label: 'Whitepapers' },
    { path: '/resources', label: 'Resources' },
    { path: '/engineering-advancement-medical-science', label: 'Medical Science Research' },
    { path: '/environmental-research', label: 'Environmental Research' },
    { path: '/compliance', label: 'Compliance' },
    { path: '/news-updates', label: 'News & Updates' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;
  const isMoreActive = moreNav.some((item) => isActive(item.path));

  return (
    <header className="sticky top-0 z-50 bg-[#1a2f4a]/95 border-b border-blue-800/30 backdrop-blur-md">
      <div className="container mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between gap-4 lg:gap-6">
          <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="header-logo-link" aria-label="SALAR Technologies LLC home">
            <span
              aria-hidden="true"
              className="flex items-center justify-center rounded-md font-bold tracking-tight text-white shrink-0"
              style={{
                width: 40,
                height: 40,
                background: 'linear-gradient(135deg, #1B6EC2 0%, #0A3D8F 100%)',
                boxShadow: '0 4px 14px rgba(27,110,194,0.4), inset 0 1px 0 rgba(255,255,255,0.18)',
                fontSize: 15,
                letterSpacing: '0.5px',
              }}
            >
              ST
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="text-white font-bold text-base tracking-wide group-hover:text-cyan-300 transition-colors">SALAR</span>
              <span className="text-cyan-300/80 text-[10px] tracking-widest uppercase">Technologies LLC</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center min-w-0" data-testid="desktop-nav">
            {primaryNav.map((item) => (
              <Link
                key={item.path}
                to={item.path} reloadDocument={item.path.endsWith('.html')}
                className={`px-3 py-2 text-sm font-medium transition-colors rounded-md whitespace-nowrap ${isActive(item.path) ? 'text-white bg-blue-600' : 'text-blue-100 hover:text-white hover:bg-blue-700/50'}`}
              >
                {item.label}
              </Link>
            ))}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="header-more-trigger"
                  className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 ${isMoreActive ? 'text-white bg-blue-600' : 'text-blue-100 hover:text-white hover:bg-blue-700/50'}`}
                >
                  More
                  <ChevronDown size={14} className="opacity-80" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8} className="bg-[#0F1A2B] border border-blue-500/40 text-blue-100 min-w-[220px]" data-testid="header-more-content">
                {moreNav.map((item) => (
                  <DropdownMenuItem key={item.path} asChild className="text-blue-100 focus:bg-cyan-600 focus:text-white data-[highlighted]:bg-cyan-600 data-[highlighted]:text-white cursor-pointer">
                    <Link to={item.path} reloadDocument={item.path.endsWith('.html')} className={isActive(item.path) ? 'text-white font-semibold' : ''}>
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/contact"
              className={`hidden sm:inline-flex items-center px-4 py-2 text-sm font-semibold rounded-md transition-colors whitespace-nowrap ${isActive('/contact') ? 'bg-cyan-500 text-[#0a1628]' : 'bg-cyan-500/15 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/25 hover:border-cyan-300/70'}`}
              data-testid="header-contact-cta"
            >
              Contact
            </Link>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden inline-flex items-center gap-2 px-3 py-2 text-blue-100 hover:text-white border border-blue-500/30 hover:border-cyan-400/60 rounded-md transition-colors"
              aria-label="Toggle navigation menu"
              aria-expanded={isMenuOpen}
              data-testid="header-menu-toggle"
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
              <span className="hidden sm:inline text-sm font-medium">{isMenuOpen ? 'Close' : 'Menu'}</span>
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 space-y-1 max-h-[70vh] overflow-y-auto" data-testid="mobile-nav">
            {mobileNav.map((item) => (
              <Link
                key={item.path}
                to={item.path} reloadDocument={item.path.endsWith('.html')}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-4 py-2 text-sm font-medium transition-colors rounded-md ${isActive(item.path) ? 'text-white bg-blue-600' : 'text-blue-100 hover:text-white hover:bg-blue-700/50'}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;

